import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { DatePicker } from 'v-calendar'
import { ref, computed, watch } from 'vue'
import { addMonths, format } from 'date-fns'
import SelectionCalendarYear from './SelectionCalendarYear.vue'
import { useI18n } from 'vue-i18n'

type Props = {
  startDate?: string | number
  endDate?: string | number
}

type Emits = {
  (e: 'dateChanged', value: { start?: string; end?: string }): void
  (e: 'dragChanged', value: { start?: string; end?: string }): void
}

const formatting = 'yyyy-MM-dd'

export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarViewNew',
  props: {
    startDate: { default: undefined },
    endDate: { default: undefined }
  },
  emits: ["dateChanged", "dragChanged"],
  setup(__props: any, { emit: __emit }) {

const maxPage = computed(() => {
  const today = new Date()
  return new Date(addMonths(today, 1))
})

const maxDate = computed(() => {
  return new Date()
})

const props = __props

const { locale } = useI18n()
const masks = {
  title: 'MMMM',
  weekdays: 'WWW'
}

const emits = __emit

const yearLabel = ref(format(new Date(), 'yyyy').toString())

const picker = ref()
const range = ref({
  start: props.startDate ? new Date(props.startDate) : undefined,
  end: props.endDate ? new Date(props.endDate) : undefined
})

const attributes = {
  key: 'selection',
  highlight: {
    start: {
      fillMode: 'solid',
      style: {
        background: '#25CED9'
      },
      contentStyle: {
        color: 'black'
      }
    },
    base: {
      fillMode: 'light',
      style: {
        background: '#C2F3EE'
      }
    },
    end: {
      fillMode: 'solid',
      style: {
        background: '#25CED9'
      },
      contentStyle: {
        color: 'black'
      }
    }
  }
}

function updatePages(pages: any[]) {
  const leftPaneYear = pages[0].year
  const rightPaneYear = pages[1].year
  if (leftPaneYear !== rightPaneYear) {
    yearLabel.value = `${leftPaneYear + '/' + rightPaneYear.toString().slice(-2)}`
  } else {
    yearLabel.value = `${leftPaneYear}`
  }
}

function updateDrag(drag: { start: Date; end: Date }) {
  emits('dragChanged', {
    start: format(drag.start, formatting),
    end: format(drag.end, formatting)
  })
}

watch(
  () => [props.startDate, props.endDate],
  async (values) => {
    if (!values[0] || !values[1]) return
    range.value = {
      start: new Date(values[0]),
      end: new Date(values[1])
    }
    await picker.value.move(new Date(values[0]), { position: 1 })
  }
)

function updateValue(value: { start?: string; end?: string }) {
  var start, end
  if (value.start) {
    start = format(new Date(value.start), formatting)
  }
  if (value.end) {
    end = format(new Date(value.end), formatting)
  }
  emits('dateChanged', { start, end })
}

async function updateFromPage() {
  if (picker.value.canMovePrev) {
    await picker.value.movePrev()
  }
}

async function updateToPage() {
  if (picker.value.canMovePrev) {
    await picker.value.moveNext()
  }
}

async function moveToPreviousYear() {
  await picker.value.moveBy(-12)
}

async function moveToNextYear() {
  await picker.value.moveBy(12)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(SelectionCalendarYear, {
      label: yearLabel.value,
      "left-arrow-disabled": false,
      "right-arrow-disabled": false,
      onLeftArrowPressed: moveToPreviousYear,
      onRightArrowPressed: moveToNextYear
    }, null, 8, ["label"]),
    _createVNode(_unref(DatePicker), {
      ref_key: "picker",
      ref: picker,
      modelValue: range.value,
      "onUpdate:modelValue": [
        _cache[0] || (_cache[0] = ($event: any) => ((range).value = $event)),
        updateValue
      ],
      modelModifiers: { range: true },
      mode: "date",
      "show-iso-weeknumbers": "",
      columns: 2,
      attr: attributes,
      "max-page": maxPage.value,
      "max-date": maxDate.value,
      locale: _unref(locale),
      masks: masks,
      "show-navigate": 'false',
      leftArrowDisabled: false,
      rightArrowDisabled: true,
      "is-required": true,
      "onUpdate:fromPage": updateFromPage,
      "onUpdate:toPage": updateToPage,
      "onUpdate:pages": updatePages,
      onDrag: updateDrag
    }, null, 8, ["modelValue", "max-page", "max-date", "locale"])
  ], 64))
}
}

})